import type {
  CompareObject,
  ComparisonObjectValue,
} from "../../../services/api/instances/comparisonAPI/types";
import type { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import type { Model } from "../../../services/api/instances/modelsApi/types";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";

const getSubstringAfterFixedPart = (
  input: string,
  fixedPart: string,
): string => {
  return input.replace(fixedPart, "");
};

export const getModel = (
  models: Model[],
  value: CompareObject[],
  id: string,
): Model | null => {
  if (!models) {
    return null;
  }
  const currentValue = value.find((item) => item.id === id);
  const model = models.find(
    (item) => item?.model_name === currentValue?.model,
  ) as Model;
  if (model) {
    return model;
  }
  return null;
};

export const replaceItemById = (
  objects: StorageObject<ComparisonObjectValue>[],
  newItem: StorageObject<ComparisonObjectValue>,
): StorageObject<ComparisonObjectValue>[] =>
  objects.map((item) =>
    item.id === newItem.id
      ? newItem
      : {
          ...item,
          ...Object.fromEntries(
            Object.entries(item).map(([key, value]) =>
              Array.isArray(value)
                ? [key, replaceItemById(value, newItem)]
                : [key, value],
            ),
          ),
        },
  );

export const checkModelExist = (value: CompareObject[]) => {
  value.find((item) => item.model === "" || item.model === undefined);
  return !!value;
};

export const generateUniqueId = (): string => {
  return "id-" + Math.random().toString(36).slice(2, 9) + "-" + Date.now();
};

export const updateCompareObject = <T extends keyof CompareObject>(
  obj: CompareObject,
  key: T,
  value: CompareObject[T],
): CompareObject => {
  return {
    ...obj,
    [key]: value,
  };
};

export const addMessageToArray = (
  obj: CompareObject,
  newMessage: ChatCompletionsMessageObject,
): CompareObject => {
  const updatedMessages = [...obj.messages, newMessage];

  return updateCompareObject(obj, "messages", updatedMessages);
};

export const sortModelsByServerType = (models: Model[]): Model[] => {
  const groupOrder = ["openai", "deepseek", "Yandex", "onprem-rmd", "qwen"];

  return models.sort((a, b) => {
    const indexA = groupOrder.indexOf(a.server_type);
    const indexB = groupOrder.indexOf(b.server_type);
    return indexA - indexB;
  });
};

export const getLastSystemInstructions = (objects: CompareObject[]) => {
  if (!objects.length) {
    return null;
  }
  const object = objects[0].messages
    .filter((role) => role.role === "system")
    .at(-1);
  return object?.content;
};

export const deleteInstructions = (
  messages: ChatCompletionsMessageObject[],
): ChatCompletionsMessageObject[] => {
  return messages.filter((message) => message.role !== "system");
};

export const addNewMessageObject = (
  messages: ChatCompletionsMessageObject[],
  role: string,
  content: string,
): ChatCompletionsMessageObject[] => {
  let newMessages = [...messages];
  const newMessage = {
    role: role,
    content: content,
  };

  if (!newMessages.length && role === "user") {
    newMessages.push(newMessage);
    return newMessages;
  }
  switch (role) {
    case "system":
    case "developer": {
      const systemMessage = newMessages.find(
        (message) => message.role === "system" || message.role === "developer",
      );
      if (systemMessage) {
        newMessages.splice(0, 1, newMessage);
      } else {
        newMessages.unshift(newMessage);
      }
      return newMessages;
    }
    case "user": {
      const lastMessage = newMessages.at(-1);
      if (lastMessage?.role === "user") {
        newMessages.splice(0, 1, newMessage);
      } else {
        newMessages = [...newMessages, newMessage];
      }
      return newMessages;
    }
    case "assistant": {
      const lastMessage = newMessages.at(-1);
      if (lastMessage?.role === "user") {
        newMessages = [...newMessages, newMessage];
      } else {
        newMessages.splice(0, 1, newMessage);
      }
      return newMessages;
    }
    default: {
      return newMessages;
    }
  }
};
